import React, { useEffect, useState } from 'react';
import { SkeletonTable } from '../../components/ui/Skeleton';
import AccountLayout from './AccountLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatusBadge from './components/StatusBadge';
import { EmptyState } from '../../components/app2';
import AlertService from '../../utils/alertService';
import billingService, { formatMxn, formatDateLong } from '../../utils/billingService';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'paid', label: 'Pagadas' },
  { value: 'open', label: 'Abiertas' },
  { value: 'draft', label: 'En borrador' },
  { value: 'uncollectible', label: 'No cobrables' },
  { value: 'void', label: 'Anuladas' },
];

const STATUS_TONE = {
  paid: 'success',
  open: 'warning',
  draft: 'muted',
  uncollectible: 'error',
  void: 'muted',
};

const STATUS_LABEL = {
  paid: 'Pagada',
  open: 'Abierta',
  draft: 'Borrador',
  uncollectible: 'No cobrable',
  void: 'Anulada',
};

const PAGE_SIZE = 25;

const AccountBilling = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await billingService.listInvoices({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        status: statusFilter || undefined,
      });
      setInvoices(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading invoices', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePayment = async () => {
    try {
      setPortalLoading(true);
      const { url } = await billingService.createPortalSession({
        returnUrl: window.location.href,
      });
      if (url) window.location.href = url;
    } catch (err) {
      AlertService?.error(
        'No se pudo abrir el portal',
        err?.response?.data?.error?.message || err?.message,
      );
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <AccountLayout
      title="Facturación"
      subtitle="Historial de facturas y gestión de tu método de pago"
      headerActions={
        <Button variant="primary" onClick={handleManagePayment} disabled={portalLoading}>
          {portalLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Abriendo...
            </>
          ) : (
            <>
              <Icon name="CreditCard" size={16} className="mr-2" />
              Gestionar método de pago
            </>
          )}
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 a2-card p-4">
          <Icon name="Filter" size={16} className="a2-t-muted" />
          <span className="text-sm a2-t-body">Estado:</span>
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
            className="a2-input"
            style={{ width: 'auto', minWidth: 170, padding: '9px 12px', fontSize: '.88rem' }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Invoice list */}
        <div className="a2-card overflow-hidden">
          {loading ? (
            <SkeletonTable rows={6} cols={4} />
          ) : invoices.length === 0 ? (
            <EmptyState
              icon="receipt_long"
              title="No hay facturas todavía"
              text="Las facturas aparecerán aquí cuando se realice el primer cobro."
              className="a2-card"
              style={{ border: 'none' }}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="a2-surface-2 a2-t-muted text-left">
                    <th className="px-4 py-3 font-medium">Factura</th>
                    <th className="px-4 py-3 font-medium">Período</th>
                    <th className="px-4 py-3 font-medium">Fecha de pago</th>
                    <th className="px-4 py-3 font-medium text-right">Monto</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="px-4 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="a2-divide">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:a2-surface-2">
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium a2-t-heading">
                          {inv.number || `Factura #${inv.id}`}
                        </div>
                        {inv.plan_name_snapshot && (
                          <div className="text-xs a2-t-muted">{inv.plan_name_snapshot}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top a2-t-body">
                        {formatDateLong(inv.period_start)}
                        <br />
                        <span className="a2-t-faint">a</span>{' '}
                        {formatDateLong(inv.period_end)}
                      </td>
                      <td className="px-4 py-3 align-top a2-t-body">
                        {inv.paid_at ? formatDateLong(inv.paid_at) : '—'}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="font-semibold a2-t-heading">
                          {formatMxn(inv.amount_paid || inv.amount_due)}
                        </div>
                        {inv.discount_amount > 0 && (
                          <div className="text-xs a2-t-ok">
                            -{formatMxn(inv.discount_amount)} desc.
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusBadge
                          tone={STATUS_TONE[inv.status] || 'muted'}
                          label={STATUS_LABEL[inv.status] || inv.status}
                        />
                        {inv.failure_reason && inv.status !== 'paid' && (
                          <div className="text-xs a2-t-danger mt-1">{inv.failure_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="inline-flex items-center gap-1">
                          {inv.hosted_invoice_url && (
                            <a
                              href={inv.hosted_invoice_url}
                              target="_blank"
                              rel="noreferrer"
                              className="a2-t-brand hover:underline text-sm inline-flex items-center gap-1"
                              title="Ver factura en Stripe"
                            >
                              <Icon name="ExternalLink" size={14} /> Ver
                            </a>
                          )}
                          {inv.invoice_pdf_url && (
                            <a
                              href={inv.invoice_pdf_url}
                              target="_blank"
                              rel="noreferrer"
                              className="a2-t-brand hover:underline text-sm inline-flex items-center gap-1 ml-3"
                              title="Descargar PDF"
                            >
                              <Icon name="Download" size={14} /> PDF
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {invoices.length === PAGE_SIZE || page > 1 ? (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Icon name="ChevronLeft" size={16} className="mr-1" />
              Anterior
            </Button>
            <span className="text-sm a2-t-muted">Página {page}</span>
            <Button
              variant="outline"
              disabled={invoices.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
              <Icon name="ChevronRight" size={16} className="ml-1" />
            </Button>
          </div>
        ) : null}
      </div>
    </AccountLayout>
  );
};

export default AccountBilling;
